import React, { useEffect, useRef } from "react";
import * as echarts from "echarts";
import useVentasMiembros from "../../../hooks/administrador/adheridos/useVentasMiembros";
import "../../../styles/administrador.css";

const VentasMiembros = ({ year, mes, token, idAdministrador }) => {
  const chartRef = useRef(null);
  const { data, loading, error } = useVentasMiembros(
    year,
    mes,
    token,
    idAdministrador
  );

  useEffect(() => {
    if (loading || !chartRef.current) return;

    if (error || !Array.isArray(data) || data.length === 0) {
      if (chartRef.current) {
        chartRef.current.innerHTML =
          "<p style='text-align: center; color: black;'>No hay datos disponibles</p>";
      }
      return;
    }

    const chartDom = chartRef.current;
    let myChart = echarts.getInstanceByDom(chartDom);
    if (myChart) myChart.dispose();
    myChart = echarts.init(chartDom);

    const colorPalette = [
      "#d1dc5a",
      "#6ac5cb",
      "#45444e",
      "#a3b85b",
      "#3a6b74",
      "#7d8a2e",
      "#3d9199",
      "#62615e",
      "#879b4a",
      "#2b5258",
    ];

    const predefinedActorIds = [83, 5947, 131, 587, 94, 5309, 18, 63, 22, 5967];
    const chartData = [];
    let othersTotal = 0;

    const predefinedActorsMap = new Map(
      predefinedActorIds.map((id) => [id, null])
    );

    data.forEach((item) => {
      const cantidad = parseFloat(item.cantidad_total);
      if (predefinedActorsMap.has(parseInt(item.id_actor))) {
        predefinedActorsMap.set(parseInt(item.id_actor), {
          ...item,
          cantidad,
        });
      } else {
        othersTotal += cantidad;
      }
    });

    predefinedActorsMap.forEach((value) => {
      if (value) {
        chartData.push(value);
      }
    });

    if (othersTotal > 0) {
      chartData.push({
        id_actor: "OTROS",
        razon_social: "OTROS",
        cantidad: othersTotal,
      });
    }

    const totalCantidad = chartData.reduce(
      (sum, item) => sum + (item.cantidad || 0),
      0
    );

    const totalFormateado = totalCantidad.toLocaleString("es-ES");

    const option = {
      title: {
        text: "Ventas por miembro adherido",
        subtext: `TOTAL ACUMULADO VENTAS: ${totalFormateado}`,
        left: "center",
        top: "10",
        textStyle: {
          fontSize: 18,
          fontWeight: "bold",
        },
        subtextStyle: {
          fontSize: 12,
          fontWeight: "bold",
        },
      },
      tooltip: {
        enable: false,
        show: false,
        trigger: "item",
        formatter: function (params) {
          const percent = Math.round(params.percent * 10) / 10;
          return `${params.name}: ${params.value.toLocaleString(
            "es-ES"
          )} (${percent}%)`;
        },
      },
      legend: {
        show: true,
        type: "scroll",  
        orient: "horizontal",  
        left: "center",  
        bottom: "10",  
        textStyle: {
          fontSize: 12,
          color: "#333",
        },
        formatter: function (name) {
          const item = chartData.find((item) => item.razon_social === name);
          const cantidad = item ? item.cantidad.toLocaleString("es-ES") : 0;
          const shortenedName =
            name.length > 13 ? `${name.slice(0, 13)}...` : name;  
          return `${shortenedName}\n${cantidad} [U]`;  
        },
        pageIconColor: "#333",  
        pageIconSize: 23,  
        pageTextStyle: {
          fontSize: 12,
          color: "#333",
        },
      },
      graphic: {
        type: "group",
        left: "center",
        top: "center",
        children: [
          {
            type: "text",
            id: "centerText",
            left: "center",
            top: "center",
            style: {
              text: `Total\n${totalFormateado}`,
              textAlign: "center",
              fontSize: 16,
              fontWeight: "bold",
              lineHeight: 20,
            },
          },
        ],
      },
      series: [
        {
          name: "Ventas",
          type: "pie",
          radius: ["40%", "70%"],  
          center: ["50%", "50%"],
          data: chartData.map((item, index) => ({
            name: item.razon_social,
            value: item.cantidad,
            itemStyle: {
              color: colorPalette[index % colorPalette.length],
              borderColor: "#fff",
              borderWidth: 4,
            },
          })),
          label: {
            show: false,
          },
          labelLine: {
            show: false,
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: "rgba(0, 0, 0, 0.5)",
            },
          },
        },
      ],
    };

    myChart.setOption(option);

     
    myChart.on("click", function (params) {
      if (params.componentType === "series") {
        myChart.setOption({
          graphic: {
            children: [
              {
                type: "text",
                id: "centerText",
                left: "center",
                top: "center",
                style: {
                  text: `${params.name.replace(
                    /(.{15})/g,
                    "$1\n"
                  )}\n${params.value.toLocaleString("es-ES")} [U]`,  
                  textAlign: "center",
                  fontSize: 14,  
                  fontWeight: "bold",  
                  lineHeight: 18,  
                },
              },
            ],
          },
        });
      }
    });

     
    myChart.on("mouseout", function () {
      myChart.setOption({
        graphic: {
          children: [
            {
              type: "text",
              id: "centerText",
              left: "center",
              top: "center",
              style: {
                text: `Total\n${totalFormateado}`,
                textAlign: "center",
                fontSize: 16,
                fontWeight: "bold",
                lineHeight: 20,
              },
            },
          ],
        },
      });
    });

    const updateChart = () => {
      myChart.resize();
    };

    window.addEventListener("resize", updateChart);

    return () => {
      window.removeEventListener("resize", updateChart);
      if (myChart && !myChart.isDisposed()) {
        myChart.dispose();
      }
    };
  }, [data, loading, error]);

  if (loading) {
    return (
      <div className="grafica loading">
        Cargando datos de ventas por miembros...
      </div>
    );
  }

  return <div className="grafica" ref={chartRef}></div>;
};

export default VentasMiembros;
